import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import FieldWrapper from '../../../components/formInputs/FieldWrapper';
import SectionHeader from '../../../components/SectionHeader';
import Button from '../../../components/buttons/Button';
import { useMemo, useRef } from 'react';
import { scrollToError } from '../../../utils/HelperFunctions';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IFaqsForm } from '../types/interfaces';
import useFormsIntegrationHelpers from '../../../hooks/useFormsIntegrationHelpers';
import FormDynamicSkeleton from '../../../components/loaders/FormDynamicSkeleton';
import { customStyles } from '../../../utils/SelectStyles';
import { useQuery } from '@tanstack/react-query';
import { generalGet } from '../../../API/api';
import { makesSelectProps } from '../../../utils/selectOptionsProps';
import useModelSelectOptions from '../../../hooks/useModelSelectOptions';

const CreateFAQForm = () => {
    const { id } = useParams();
    const formRef = useRef(null);
    const { t } = useTranslation();

    const { apiData, getDataLoading, handleSubmit, submitLoading } = useFormsIntegrationHelpers({
        queryKey: ['faqs', id],
        invalidateQueryKey: ['faqs'],
        id,
        singleGetApi: `/admin/cms/faqs/${id}`,
        addApi: '/admin/cms/faqs',
        editApi: `/admin/cms/faqs/${id}`,
        listRoute: '/faqs',
        itemName: t('faqs')
    });

    const { modelOptions: vehicleMakesOptions } = useModelSelectOptions(makesSelectProps());

    const validationSchema = Yup.object({
        title: Yup.string().required(t('required')),
        description: Yup.string().required(t('required')),
        order: Yup.string()
            .required(t('required'))
            .matches(/^\d+$/, t('must_no')),
        vehicle_make_id: Yup.string().required(t('required'))
    });

    const initialValues: IFaqsForm = {
        ...apiData,
        
        title: apiData?.terms_and_conditions?.title || '',
        description: apiData?.terms_and_conditions?.description || '',
        order: apiData?.terms_and_conditions?.order || '',
        vehicle_make_id: apiData?.terms_and_conditions?.vehicle_make_id || (vehicleMakesOptions?.length == 1 ? vehicleMakesOptions[0]?.value : '')
    };

    if (getDataLoading)
        return (
            <FormDynamicSkeleton
                sections={[
                    [
                        { columns: 2, items: 1 },
                        { columns: 2, items: 1, type: 'textarea' },
                        { columns: 2, items: 2 },
                    ]
                ]}
                buttons={1}
            />
        );

    return (
        <div className={'form_section'} ref={formRef}>
            <Formik
                validateOnMount
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values, { setErrors }) => {
                    let value = { ...values, _method: 'POST' };
                    if (id) {
                        value = { ...value, _method: 'PUT' };
                    }
                    handleSubmit({ ...value }, setErrors);
                }}
            >
                {(formik) => (
                    <>
                        <Form>
                            <SectionHeader title={t('terms_and_conditions_details')} customStyle="last">
                                <div className="inputs_grid">
                                    <FieldWrapper
                                        customPadding
                                        title={t('faq_question')}
                                        inputPlaceholder={t('faq_question')}
                                        inputName={`title`}
                                        input
                                        gridSize={6}
                                    />
                                    <FieldWrapper
                                        title={t('faq_answer')}
                                        inputName={`description`}
                                        inputPlaceholder={t('faq_answer')}
                                        textArea
                                        customPadding
                                    />
                                    <FieldWrapper
                                        customPadding
                                        title={t('faq_order')}
                                        inputPlaceholder={t('faq_order')}
                                        inputName={`order`}
                                        input
                                        gridSize={6}
                                    />
                                    {vehicleMakesOptions?.length > 1 && <FieldWrapper
                                        customPadding
                                        title={t('vehicle_make')}
                                        inputPlaceholder={t('vehicle_make')}
                                        inputName={`vehicle_make_id`}
                                        options={vehicleMakesOptions}
                                        selectStyle={customStyles}
                                        select
                                        gridSize={6}
                                    />}
                                </div>
                            </SectionHeader>
                            <div className="form_button reverse">
                                <Button
                                    loading={submitLoading}
                                    onClick={() => {
                                        scrollToError(!formik.isValid, formRef);
                                    }}
                                >
                                    <span className="bold">{t('save')}</span>
                                </Button>
                            </div>
                        </Form>
                    </>
                )}
            </Formik>
        </div>
    );
};

export default CreateFAQForm;
